import { Controller } from "@hotwired/stimulus"
import { heicTo, isHeic } from "heic-to"

// Connects to data-controller="photo-preview"
export default class extends Controller {
  static targets = ["preview"]

  static values = {
    previewText: String,
    fileText: String,
    heicConvertingText: String,
    heicErrorText: String,
    unsupportedFormatText: String
  }

  connect() {
    this.changeHandler = this.previewPhoto.bind(this)
    this.element.addEventListener('change', this.changeHandler)
    this.activeObjectUrl = null
  }

  disconnect() {
    this.element.removeEventListener('change', this.changeHandler)
    this.revokeObjectURL()
  }

  async previewPhoto(event) {
    const file = event.target.files?.[0]

    if (!file) {
      this.removePreview()
      return
    }

    let blobForPreview = file
    let fileName = file.name

    try {
      if (await this.shouldConvertHeic(file)) {
        this.renderMessage(this.heicConvertingText)
        blobForPreview = await this.convertHeicToJpeg(file)
        fileName = this.convertedFileName(file.name)
      }

      if (!this.isPreviewableImage(blobForPreview)) {
        this.renderMessage(this.unsupportedFormatText)
        return
      }

      this.renderPreview(blobForPreview, fileName)
    } catch (error) {
      console.error('Photo preview conversion error:', error)
      this.renderMessage(this.heicErrorText)
    }
  }

  async shouldConvertHeic(file) {
    try {
      if (await isHeic(file)) {
        return true
      }
    } catch (error) {
      console.warn('Unable to detect HEIC format via signature check:', error)
    }

    const extension = this.fileExtension(file.name)
    return ['heic', 'heif'].includes(extension)
  }

  async convertHeicToJpeg(file) {
    return await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.9
    })
  }

  renderPreview(blob, fileName) {
    const previewContainer = this.ensurePreviewContainer()

    this.revokeObjectURL()
    const objectUrl = URL.createObjectURL(blob)
    this.activeObjectUrl = objectUrl

    const markup = `
      <div class="d-flex align-items-center gap-3">
        <div class="photo-preview-image">
          <img src="${objectUrl}" 
               class="rounded" 
               style="width: 120px; height: 120px; object-fit: contain;" 
               alt="${this.escapeHTML(this.previewText)}">
        </div>
        <div>
          <p class="mb-1 text-muted small">
            <i class="fa-solid fa-eye me-1"></i>
            ${this.escapeHTML(this.previewText)}
          </p>
          <p class="mb-0 text-muted small">
            ${this.escapeHTML(this.fileText)} ${this.escapeHTML(fileName)} (${this.formatFileSize(blob.size)})
          </p>
        </div>
      </div>
    `

    previewContainer.innerHTML = markup

    const imageElement = previewContainer.querySelector('img')
    if (imageElement) {
      imageElement.addEventListener('load', () => this.revokeObjectURL(), { once: true })
    }
  }

  renderMessage(message) {
    const previewContainer = this.ensurePreviewContainer()
    previewContainer.innerHTML = `
      <div class="d-flex align-items-center gap-2 text-muted small">
        <i class="fa-solid fa-circle-info"></i>
        <span>${this.escapeHTML(message)}</span>
      </div>
    `
  }

  ensurePreviewContainer() {
    let previewContainer = this.element.parentElement?.querySelector('.photo-preview-container')

    if (!previewContainer) {
      previewContainer = document.createElement('div')
      previewContainer.className = 'photo-preview-container mt-2'
      this.element.parentElement?.insertBefore(previewContainer, this.element.nextSibling)
    }

    return previewContainer
  }

  removePreview() {
    this.revokeObjectURL()
    const previewContainer = this.element.parentElement?.querySelector('.photo-preview-container')
    if (previewContainer) {
      previewContainer.remove()
    }
  }

  revokeObjectURL() {
    if (this.activeObjectUrl) {
      URL.revokeObjectURL(this.activeObjectUrl)
      this.activeObjectUrl = null
    }
  }

  fileExtension(name) {
    if (!name || !name.includes('.')) {
      return ''
    }
    return name.split('.').pop().toLowerCase()
  }

  convertedFileName(name) {
    const base = name.includes('.') ? name.substring(0, name.lastIndexOf('.')) : name
    const safeBase = base && base.trim().length > 0 ? base : 'converted-image'
    return `${safeBase}.jpg`
  }

  isPreviewableImage(blob) {
    return Boolean(blob?.type && blob.type.startsWith('image/'))
  }

  formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return this.formatWithUnit(0, 'byte', 0)
    }

    const units = [
      { threshold: 1024 ** 3, unit: 'gigabyte' },
      { threshold: 1024 ** 2, unit: 'megabyte' },
      { threshold: 1024, unit: 'kilobyte' }
    ]

    for (const { threshold, unit } of units) {
      if (bytes >= threshold) {
        return this.formatWithUnit(bytes / threshold, unit, 2)
      }
    }

    return this.formatWithUnit(bytes, 'byte', 0)
  }

  formatWithUnit(value, unit, fractionDigits) {
    return new Intl.NumberFormat(this.locale, {
      style: 'unit',
      unit,
      unitDisplay: 'short',
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits === 0 ? 0 : 1
    }).format(value)
  }

  escapeHTML(value) {
    const div = document.createElement('div')
    div.textContent = value ?? ''
    return div.innerHTML
  }

  get previewText() {
    return this.previewTextValue || this.translate('photo_preview.preview_text_default')
  }

  get fileText() {
    return this.fileTextValue || this.translate('photo_preview.file_label_default')
  }

  get heicConvertingText() {
    return this.heicConvertingTextValue || this.translate('photo_preview.converting')
  }

  get heicErrorText() {
    return this.heicErrorTextValue || this.translate('photo_preview.conversion_error')
  }

  get unsupportedFormatText() {
    return this.unsupportedFormatTextValue || this.translate('photo_preview.unsupported_format')
  }

  get locale() {
    return document.documentElement.lang || 'en'
  }

  translate(key) {
    if (window.I18n?.t) {
      return window.I18n.t(key)
    }
    return key
  }
}
